import { useEditor, EditorContent } from '@tiptap/react';
import './tiptap-editor.css';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import { Extension } from '@tiptap/core';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table';
import { TableHeader } from '@tiptap/extension-table';
import { useEffect, useRef, useCallback, useState } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, ListChecks, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Unlink, Undo2, Redo2, Minus, Quote, Table as TableIcon,
  Highlighter, Type, Palette, ChevronDown, TableProperties, Trash2,
  Plus, Columns, Rows
} from 'lucide-react';

const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7',
  '#dc2626', '#ea580c', '#d97706', '#65a30d', '#16a34a',
  '#0891b2', '#2563eb', '#7c3aed', '#c026d3', '#e11d48',
];

const HIGHLIGHTS = [
  '#fef08a', '#bbf7d0', '#bfdbfe', '#e9d5ff', '#fecdd3',
  '#fed7aa', '#ccfbf1', '#dbeafe', '#fce7f3', '#f5f5f4',
];

const EnterAsHardBreak = Extension.create({
  name: 'enterAsHardBreak',
  addKeyboardShortcuts() {
    return {
      Enter: () => this.editor.commands.setHardBreak(),
    };
  },
});

const slugifyTestId = (value = '') => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function ToolbarButton({ onClick, active, disabled, title, testId, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testId}
      className={`p-1.5 rounded-md transition-colors ${active ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'} ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" />;
}

function ColorDropdown({ colors, onSelect, activeColor, icon: Icon, title, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(!open); }}
        title={title}
        aria-label={title}
        data-testid={testId}
        className={`flex items-center gap-0.5 p-1.5 rounded-md transition-colors ${activeColor ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}
      >
        <Icon className="w-3.5 h-3.5" />
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-50 grid grid-cols-5 gap-1" style={{ width: 160 }}>
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onSelect(c); setOpen(false); }}
              className="w-6 h-6 rounded-md border border-slate-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: c }}
              title={c}
              aria-label={`${title} ${c}`}
              data-testid={`${testId}-option-${slugifyTestId(c)}`}
            />
          ))}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onSelect(null); setOpen(false); }}
            className="col-span-5 text-xs text-slate-500 hover:text-slate-700 py-1 mt-1 border-t border-slate-100"
            aria-label={`Remover ${title.toLowerCase()}`}
            data-testid={`${testId}-remove-option`}
          >
            Remover cor
          </button>
        </div>
      )}
    </div>
  );
}

function TableDropdown({ editor, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const isInTable = editor?.isActive('table');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen(!open); }}
        title="Tabela"
        aria-label="Tabela"
        data-testid={testId}
        className={`flex items-center gap-0.5 p-1.5 rounded-md transition-colors ${isInTable ? 'bg-blue-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}
      >
        <TableIcon className="w-3.5 h-3.5" />
        <ChevronDown className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50 min-w-[180px]">
          {!isInTable && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" aria-label="Inserir tabela 3x3" data-testid={`${testId}-insert-table`}><Plus className="w-3.5 h-3.5" />Inserir tabela 3x3</button>
          )}
          {isInTable && (<>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().addColumnAfter().run(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" aria-label="Adicionar coluna" data-testid={`${testId}-add-column`}><Columns className="w-3.5 h-3.5" />Adicionar coluna</button>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().addRowAfter().run(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" aria-label="Adicionar linha" data-testid={`${testId}-add-row`}><Rows className="w-3.5 h-3.5" />Adicionar linha</button>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().deleteColumn().run(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2" aria-label="Remover coluna" data-testid={`${testId}-delete-column`}><Columns className="w-3.5 h-3.5" />Remover coluna</button>
            <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().deleteRow().run(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2" aria-label="Remover linha" data-testid={`${testId}-delete-row`}><Rows className="w-3.5 h-3.5" />Remover linha</button>
            <div className="border-t border-slate-100 my-1" />
            <button type="button" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().deleteTable().run(); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2" aria-label="Remover tabela" data-testid={`${testId}-delete-table`}><Trash2 className="w-3.5 h-3.5" />Remover tabela</button>
          </>)}
        </div>
      )}
    </div>
  );
}

function Toolbar({ editor, testId }) {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL do link:', 'https://');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/80" data-testid={`${testId}-toolbar`}>
      {/* Undo/Redo */}
      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Desfazer" testId={`${testId}-toolbar-undo-button`}>
        <Undo2 className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Refazer" testId={`${testId}-toolbar-redo-button`}>
        <Redo2 className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Headings */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Título 1" testId={`${testId}-toolbar-heading-1-button`}>
        <Heading1 className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Título 2" testId={`${testId}-toolbar-heading-2-button`}>
        <Heading2 className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Título 3" testId={`${testId}-toolbar-heading-3-button`}>
        <Heading3 className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Text formatting */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Negrito" testId={`${testId}-toolbar-bold-button`}>
        <Bold className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Itálico" testId={`${testId}-toolbar-italic-button`}>
        <Italic className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Sublinhado" testId={`${testId}-toolbar-underline-button`}>
        <UnderlineIcon className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Riscado" testId={`${testId}-toolbar-strike-button`}>
        <Strikethrough className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Colors */}
      <ColorDropdown
        colors={COLORS}
        onSelect={(c) => c ? editor.chain().focus().setColor(c).run() : editor.chain().focus().unsetColor().run()}
        activeColor={editor.getAttributes('textStyle').color}
        icon={Palette}
        title="Cor do texto"
        testId={`${testId}-toolbar-text-color-button`}
      />
      <ColorDropdown
        colors={HIGHLIGHTS}
        onSelect={(c) => c ? editor.chain().focus().toggleHighlight({ color: c }).run() : editor.chain().focus().unsetHighlight().run()}
        activeColor={editor.isActive('highlight')}
        icon={Highlighter}
        title="Destaque"
        testId={`${testId}-toolbar-highlight-button`}
      />

      <ToolbarDivider />

      {/* Lists */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista" testId={`${testId}-toolbar-bullet-list-button`}>
        <List className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerada" testId={`${testId}-toolbar-ordered-list-button`}>
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive('taskList')} title="Lista de tarefas" testId={`${testId}-toolbar-task-list-button`}>
        <ListChecks className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Alignment */}
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Alinhar à esquerda" testId={`${testId}-toolbar-align-left-button`}>
        <AlignLeft className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centralizar" testId={`${testId}-toolbar-align-center-button`}>
        <AlignCenter className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Alinhar à direita" testId={`${testId}-toolbar-align-right-button`}>
        <AlignRight className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justificar" testId={`${testId}-toolbar-align-justify-button`}>
        <AlignJustify className="w-3.5 h-3.5" />
      </ToolbarButton>

      <ToolbarDivider />

      {/* Extras */}
      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Citação" testId={`${testId}-toolbar-blockquote-button`}>
        <Quote className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Linha horizontal" testId={`${testId}-toolbar-horizontal-rule-button`}>
        <Minus className="w-3.5 h-3.5" />
      </ToolbarButton>
      {editor.isActive('link') ? (
        <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} active title="Remover link" testId={`${testId}-toolbar-unlink-button`}>
          <Unlink className="w-3.5 h-3.5" />
        </ToolbarButton>
      ) : (
        <ToolbarButton onClick={addLink} title="Adicionar link" testId={`${testId}-toolbar-link-button`}>
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarButton>
      )}
      <TableDropdown editor={editor} testId={`${testId}-toolbar-table-button`} />
    </div>
  );
}

export default function TipTapEditor({ content, onChange, onBlur, readOnly = false, placeholder = '', className = '', minHeight, enterAsHardBreak = false, testId = 'tiptap-editor' }) {
  const skipNextUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-600 underline cursor-pointer' } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      ...(enterAsHardBreak ? [EnterAsHardBreak] : []),
    ],
    content: content || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (skipNextUpdate.current) { skipNextUpdate.current = false; return; }
      onChange?.(editor.getHTML());
    },
    onBlur: ({ editor }) => {
      onBlur?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== undefined) {
      const current = editor.getHTML();
      if (current !== content) {
        skipNextUpdate.current = true;
        editor.commands.setContent(content || '', false);
      }
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  if (!editor) return null;

  return (
    <div className={`tiptap-editor-wrapper bg-white ${className}`} style={minHeight ? { '--tiptap-min-height': minHeight } : undefined} data-testid={testId}>
      {!readOnly && <Toolbar editor={editor} testId={testId} />}
      <EditorContent editor={editor} className="tiptap-content" data-testid={`${testId}-content`} />
    </div>
  );
}
